import { useEffect, useState } from "react";
import { ArrowUp, ArrowDown, Pencil, Download, Save, X, Scissors, Merge } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, download } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { NoManuscript } from "./NoManuscript";

const KINDS = ["front_matter", "prologue", "chapter", "part", "section", "epilogue", "appendix", "back_matter"];

export function renderInline(text) {
  return text.split(/(\*\*\*[^*]+\*\*\*|\*\*[^*]+\*\*|\*[^*]+\*)/g).map((p, i) => {
    if (/^\*\*\*.+\*\*\*$/.test(p)) return <strong key={i}><em>{p.slice(3, -3)}</em></strong>;
    if (/^\*\*.+\*\*$/.test(p)) return <strong key={i}>{p.slice(2, -2)}</strong>;
    if (/^\*.+\*$/.test(p) && p.length > 2) return <em key={i}>{p.slice(1, -1)}</em>;
    return p;
  });
}

function Reader({ ch }) {
  return (
    <div className="manuscript text-[17px] text-slate-200 max-w-2xl" data-testid="chapter-reader">
      {ch.content.split("\n\n").map((p, i) => p.trim() === "* * *" ? <p key={i} className="text-center text-amber-500/70 my-6">* * *</p> : <p key={i} className="mb-4 indent-8">{renderInline(p)}</p>)}
    </div>
  );
}

export default function ManuscriptTab({ book, version, reload }) {
  const [sel, setSel] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [order, setOrder] = useState(null);
  const [split, setSplit] = useState(null);
  useEffect(() => { if (version) setSel((s) => version.chapters.find((c) => c.id === s?.id) || version.chapters[0]); }, [version]);
  if (!version) return <NoManuscript book={book} />;

  const save = async () => {
    try {
      const { data } = await api.put(`/books/${book.id}/chapters/${sel.id}`, draft);
      toast.success(`Saved as version ${data.number}`);
      setEditing(false); reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const doSplit = async () => {
    if (!window.confirm(`Split '${sel.title}' at paragraph ${split.at}? This saves a new version.`)) return;
    try { const { data } = await api.post(`/books/${book.id}/chapters/${sel.id}/split`, { at_paragraph: split.at, new_title: split.title, confirm: true }); toast.success(`Split saved as v${data.number}`); setSplit(null); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const merge = async () => {
    const i = version.chapters.findIndex((c) => c.id === sel.id);
    const next = version.chapters[i + 1];
    if (!window.confirm(`Merge '${sel.title}' and '${next.title}' into one section (scene break between them)? This saves a new version.`)) return;
    try { const { data } = await api.post(`/books/${book.id}/chapters/merge`, { chapter_ids: [sel.id, next.id], scene_break: true, confirm: true }); toast.success(`Merged as v${data.number}`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const move = (i, d) => { const o = [...order]; [o[i], o[i + d]] = [o[i + d], o[i]]; setOrder(o); };
  const saveOrder = async () => {
    if (!window.confirm("Save this new section order as a new version? Chapter titles are NOT renumbered automatically.")) return;
    try { const { data } = await api.post(`/books/${book.id}/chapters/reorder`, { order: order.map((c) => c.id), confirm: true }); toast.success(`Order saved as v${data.number}`); setOrder(null); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const list = order || version.chapters;
  return (
    <div className="grid lg:grid-cols-[300px_1fr] gap-6">
      <div className="panel p-3 h-fit lg:sticky lg:top-6" data-testid="chapter-list">
        <div className="flex justify-between items-center px-2 py-2">
          <p className="eyebrow">Sections ({list.length})</p>
          {order ? <span className="flex gap-1"><Button size="sm" className="h-7 bg-amber-600" onClick={saveOrder} data-testid="save-order-button">Save</Button><Button size="sm" variant="ghost" className="h-7" onClick={() => setOrder(null)} data-testid="cancel-order-button"><X size={14} /></Button></span>
            : <button className="text-xs text-amber-400" onClick={() => setOrder([...version.chapters])} data-testid="reorder-button">Reorder</button>}
        </div>
        <div className="max-h-[65vh] overflow-y-auto scrollbar-thin">
          {list.map((c, i) => (
            <div key={c.id} className={`flex items-center gap-1 rounded-lg ${sel?.id === c.id ? "bg-amber-500/10" : "hover:bg-slate-800/50"}`}>
              <button onClick={() => { setSel(c); setEditing(false); }} className="flex-1 text-left px-3 py-2 min-w-0" data-testid={`chapter-item-${i}`}>
                <p className={`text-sm truncate ${sel?.id === c.id ? "text-amber-300" : "text-slate-300"}`}>{c.title}</p>
                <p className="text-[11px] text-slate-500 font-mono">{c.kind.replace("_", " ")} · {c.word_count} w</p>
              </button>
              {order && <span className="flex flex-col pr-1"><button disabled={i === 0} onClick={() => move(i, -1)} data-testid={`move-up-${i}`}><ArrowUp size={13} /></button><button disabled={i === list.length - 1} onClick={() => move(i, 1)} data-testid={`move-down-${i}`}><ArrowDown size={13} /></button></span>}
            </div>
          ))}
        </div>
      </div>
      {sel && (
        <div className="panel p-6 sm:p-10 min-w-0">
          <div className="flex flex-wrap justify-between gap-3 mb-8">
            <div><p className="eyebrow mb-1">{sel.kind.replace("_", " ")} · {sel.word_count} words · v{version.number}</p><h2 className="font-display text-3xl">{sel.title}</h2></div>
            <div className="flex flex-wrap gap-2">
              {!editing && <Button size="sm" variant="outline" className="border-slate-700" onClick={() => { setDraft({ title: sel.title, content: sel.content, kind: sel.kind }); setEditing(true); }} data-testid="edit-chapter-button"><Pencil size={14} className="mr-1" />Edit</Button>}
              {!editing && <Button size="sm" variant="outline" className="border-slate-700" onClick={() => setSplit({ at: 2, title: `${sel.title} (Part 2)` })} data-testid="split-chapter-button"><Scissors size={14} className="mr-1" />Split</Button>}
              {!editing && version.chapters.findIndex((c) => c.id === sel.id) < version.chapters.length - 1 && <Button size="sm" variant="outline" className="border-slate-700" onClick={merge} data-testid="merge-next-button"><Merge size={14} className="mr-1" />Merge with next</Button>}
              <Button size="sm" variant="outline" className="border-slate-700" onClick={() => download(`/books/${book.id}/versions/${version.id}/export?fmt=docx&chapter_id=${sel.id}`, "chapter.docx")} data-testid="export-chapter-button"><Download size={14} className="mr-1" />DOCX</Button>
            </div>
          </div>
          {split && (
            <div className="panel p-4 mb-6 border-amber-500/30 flex flex-wrap gap-2 items-center" data-testid="split-panel">
              <span className="text-sm text-slate-300">New section starts at paragraph</span>
              <Input type="number" min={2} max={sel.content.split("\n\n").length} value={split.at} onChange={(e) => setSplit({ ...split, at: Number(e.target.value) })} className="w-20" data-testid="split-at-input" />
              <Input value={split.title} onChange={(e) => setSplit({ ...split, title: e.target.value })} className="flex-1 min-w-[180px]" data-testid="split-title-input" />
              <Button size="sm" className="bg-amber-600" onClick={doSplit} data-testid="split-confirm-button">Split</Button>
              <Button size="sm" variant="ghost" onClick={() => setSplit(null)} data-testid="split-cancel-button">Cancel</Button>
              <p className="w-full text-xs text-slate-500">Starts with: “{(sel.content.split("\n\n")[split.at - 1] || "").slice(0, 120)}…” Chapters are not renumbered automatically.</p>
            </div>
          )}
          {editing ? (
            <div className="space-y-3" data-testid="chapter-editor">
              <div className="flex gap-3"><Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} data-testid="chapter-title-input" />
                <select value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value })} className="h-9 rounded-md bg-slate-900 border border-slate-700 px-2 text-sm" data-testid="chapter-kind-select">{KINDS.map((k) => <option key={k} value={k}>{k.replace("_", " ")}</option>)}</select></div>
              <Textarea value={draft.content} onChange={(e) => setDraft({ ...draft, content: e.target.value })} className="min-h-[55vh] manuscript text-base" data-testid="chapter-content-input" />
              <p className="text-xs text-slate-500">Separate paragraphs with a blank line. Use *italic*, **bold** and a line with * * * for scene breaks. Saving creates a new version; the previous one is kept.</p>
              <div className="flex gap-2"><Button onClick={save} className="bg-amber-600 hover:bg-amber-500" data-testid="save-chapter-button"><Save size={14} className="mr-1" />Save as new version</Button><Button variant="ghost" onClick={() => setEditing(false)} data-testid="cancel-edit-button">Cancel</Button></div>
            </div>
          ) : <Reader ch={sel} />}
        </div>
      )}
    </div>
  );
}
